import React from "react";
import { ZONES } from "@/lib/handball";
import GoalGrid from "@/components/GoalGrid";
import GoalFrame from "@/components/GoalFrame";

/**
 * Shared stats visualization: KPI blocks + 9-grid heatmap + breakdown per zone/distance.
 * Used across SessionRegister (live), EvaluateView (read-only), and ShareView.
 * When `showMissFrame` is true, the miss ring around the goal is rendered with counts.
 */
export default function StatsView({
  stats,
  testIdPrefix = "stats",
  showGridInGrid = false,
  showMissFrame = false,
}) {
  const s = stats ?? {
    total: 0,
    saves: 0,
    goals: 0,
    save_pct: 0,
    misses: 0,
    placement: {},
    miss_placement: {},
    zones: {},
    distances: {},
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Skott på mål" value={s.total} testid={`${testIdPrefix}-kpi-total`} />
        <Kpi label="Räddningar" value={s.saves} testid={`${testIdPrefix}-kpi-saves`} />
        <Kpi label="Mål" value={s.goals} testid={`${testIdPrefix}-kpi-goals`} />
        <Kpi
          label="Räddning %"
          value={`${(s.save_pct ?? 0).toFixed(1)}%`}
          testid={`${testIdPrefix}-kpi-savepct`}
          accent
        />
      </div>

      <div className={showGridInGrid ? "" : "card-tactical p-6"}>
        {!showGridInGrid && (
          <div className="section-hdr mb-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="marker" />
              Målbild · Räddningsprocent per ruta
            </div>
            {showMissFrame && (
              <div
                className="text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] font-display"
                data-testid={`${testIdPrefix}-miss-count`}
              >
                Missar · {s.misses ?? 0}
              </div>
            )}
          </div>
        )}
        <div className="max-w-[640px] mx-auto">
          {showMissFrame ? (
            <GoalFrame
              stats={s.placement}
              missStats={s.miss_placement}
              showMissFrame
              testId={`${testIdPrefix}-goal-grid`}
              testIdPrefix={`${testIdPrefix}-cell`}
              missTestIdPrefix={`${testIdPrefix}-miss-cell`}
            />
          ) : (
            <GoalGrid
              stats={s.placement}
              testId={`${testIdPrefix}-goal-grid`}
              testIdPrefix={`${testIdPrefix}-cell`}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <BreakdownCard title="Per zon" data={s.zones} order={ZONES} />
        <BreakdownCard
          title="Per avstånd"
          data={s.distances}
          order={["9m", "mellan", "6m", "7m"]}
          labels={{ "9m": "9M", mellan: "MELLAN", "6m": "6M", "7m": "7M STRAFF" }}
        />
      </div>

      {s.contacts &&
        Object.values(s.contacts).some((v) => (v?.total || 0) > 0) && (
          <BreakdownCard
            title="Per kontakt"
            data={s.contacts}
            order={["yes", "no"]}
            labels={{ yes: "JA", no: "NEJ" }}
          />
        )}

      {s.lobs &&
        Object.values(s.lobs).some((v) => (v?.total || 0) > 0) && (
          <BreakdownCard
            title="Per lobb"
            data={s.lobs}
            order={["yes", "no"]}
            labels={{ yes: "JA", no: "NEJ" }}
          />
        )}
    </div>
  );
}

export function Kpi({ label, value, accent, testid }) {
  return (
    <div className="kpi" data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div
        className="kpi-value font-mono-num"
        style={{ color: accent ? "#34C759" : "#ffffff" }}
      >
        {value}
      </div>
    </div>
  );
}

export function BreakdownCard({ title, data, order, labels = {} }) {
  return (
    <div className="card-tactical p-5">
      <div className="section-hdr mb-4">
        <span className="marker" />
        {title}
      </div>
      <div className="space-y-2">
        {order.map((key) => {
          const d = data?.[key] ?? { total: 0, saves: 0, save_pct: 0 };
          const pct = d.total ? d.save_pct : 0;
          return (
            <div
              key={key}
              className="grid grid-cols-[70px_1fr_90px] items-center gap-3"
            >
              <span className="font-display font-bold uppercase tracking-widest text-sm">
                {labels[key] ?? key}
              </span>
              <div className="h-2 bg-[#171717] border border-[#27272A] relative">
                <div
                  className="h-full"
                  style={{
                    width: `${pct}%`,
                    background:
                      pct >= 66 ? "#34C759" : pct >= 33 ? "#FFD60A" : "#FF3B30",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <span className="text-xs text-zinc-400 font-mono-num text-right">
                {d.saves}/{d.total} · {d.total ? d.save_pct.toFixed(0) : 0}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
